import React, {Dispatch, SetStateAction, useMemo} from 'react';

import AemButton from '@/components/Aem/AemButton';
import {AppForm} from '@/components/AppForm';
import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import appConfig from '@/config/appConfig.typed';
import useAuthForm from '@/containers/LoginPageV2/hooks/useAuthForm';
import type {LoginErrorDetails} from '@/containers/LoginPageV2/LoginPageV2.types';
import useExternalLink from '@/hooks/useExternalLink';
import {getLoginContent} from '@/services/content/appContent.typed';
import log from '@/services/logger/log';
import {twx} from '@/utils/tailwindUtils';

import LoginErrorAlert from './LoginErrorAlert';

const USERNAME_FIELD_NAME = 'username';
const PASSWORD_FIELD_NAME = 'password';

interface LoginFormData {
    username: string;
    password: string;
}

interface LoginFormProps {
    loginError: LoginErrorDetails;
    setLoginError: Dispatch<SetStateAction<LoginErrorDetails>>;
    onLogin: (credentials: LoginFormData) => void;
}

const LoginForm = ({loginError, setLoginError, onLogin}: LoginFormProps) => {
    const forgotPasswordLinkCode = appConfig.getForgotPasswordLinkCode();
    // @ts-ignore
    const {redirect: redirectToForgotPassword} = useExternalLink({externalLinkCode: forgotPasswordLinkCode});

    const {loginEmailRegex, loginPasswordRegex} = appConfig.getLoginSettings();

    const {emailRegex, passwordRegex} = useMemo(() => {
        try {
            return {
                emailRegex: loginEmailRegex ? new RegExp(loginEmailRegex) : undefined,
                passwordRegex: loginPasswordRegex ? new RegExp(loginPasswordRegex) : undefined,
            };
        } catch (error) {
            log.info(
                `Invalid regex pattern in login settings, loginEmailRegex: ${loginEmailRegex}, loginPasswordRegex: ${loginPasswordRegex}`
            );
        }
        return {};
    }, [loginEmailRegex, loginPasswordRegex]);

    const {
        emailLabel,
        emailPlaceholder,
        emailRegexWarning,
        passwordLabel,
        passwordPlaceholder,
        passwordRegexWarning,
        requiredFieldWarning,
        showPasswordButton,
        hidePasswordButton,
        clearInputFieldButton,
        forgotPasswordButton,
        loginButton,
    } = getLoginContent();

    const {control, handleFormSubmit, handleFormKeyDown, fieldStates, clearField} = useAuthForm<LoginFormData>({
        fields: [
            {name: USERNAME_FIELD_NAME, pattern: emailRegex},
            {name: PASSWORD_FIELD_NAME, pattern: passwordRegex},
        ],
        onSubmit: ({username, password}) => {
            onLogin({username, password});
        },
        setError: setLoginError,
        defaultValues: {
            [USERNAME_FIELD_NAME]: '',
            [PASSWORD_FIELD_NAME]: '',
        },
    });

    const usernameField = fieldStates.username;
    const passwordField = fieldStates.password;

    const getWarning = (field: {isEntered: boolean; isValid: boolean}, regexWarning: any) => {
        if (!field.isEntered) return requiredFieldWarning;
        return field.isValid ? null : regexWarning;
    };

    const emailWarning = getWarning(usernameField, emailRegexWarning);
    const passwordWarning = getWarning(passwordField, passwordRegexWarning);

    const handleInputChange = (field: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (loginError) {
            setLoginError(null);
        }
        field.onChange(event);
    };

    const handleInputFocus = (fieldName: string) => () => {
        if (loginError) {
            clearField(fieldName as any);
        }
    };

    return (
        <AppForm className='m-auto w-full ml:mt-0' onSubmit={handleFormSubmit} onKeyDown={handleFormKeyDown} noValidate>
            <div className='flex flex-col gap-6'>
                {loginError && <LoginErrorAlert />}

                <AppForm.Body className='flex w-full flex-col text-left text-primary md:flex-wrap md:justify-between md:gap-0 md:[&>*]:basis-[calc(50%_-_12px)]'>
                    <AppForm.Field
                        name={USERNAME_FIELD_NAME}
                        control={control}
                        rules={{required: true, pattern: emailRegex}}
                        warningMessage={emailWarning?.title}
                    >
                        {({field}) => (
                            <>
                                <AppForm.FieldLabel className='mb-4'>{emailLabel}</AppForm.FieldLabel>
                                <AppForm.FieldInput
                                    className={usernameField.className}
                                    onChange={handleInputChange(field)}
                                    onFocus={handleInputFocus(USERNAME_FIELD_NAME)}
                                    onBlur={field.onBlur}
                                    clearButton={
                                        <AemButton
                                            className='m-0 p-0'
                                            {...clearInputFieldButton}
                                            onClick={() => clearField(USERNAME_FIELD_NAME)}
                                        />
                                    }
                                    warningIcon={<RemoteIconV2 className='h-6 w-6' icon={emailWarning} />}
                                    value={field.value}
                                    type='text'
                                    placeholder={emailPlaceholder}
                                />
                                <AppForm.FieldWarningMessage />
                            </>
                        )}
                    </AppForm.Field>

                    <AppForm.Field
                        name={PASSWORD_FIELD_NAME}
                        control={control}
                        rules={{required: true, pattern: passwordRegex}}
                        warningMessage={passwordWarning?.title}
                    >
                        {({field}) => (
                            <>
                                <AppForm.FieldLabel className='mb-4 mt-6'>{passwordLabel}</AppForm.FieldLabel>
                                <AppForm.FieldPasswordInput
                                    className={passwordField.className}
                                    onFocus={handleInputFocus(PASSWORD_FIELD_NAME)}
                                    onChange={handleInputChange(field)}
                                    onBlur={field.onBlur}
                                    showPasswordButton={<AemButton className='m-0 p-0' {...showPasswordButton} />}
                                    hidePasswordButton={<AemButton className='m-0 p-0' {...hidePasswordButton} />}
                                    value={field.value}
                                    placeholder={passwordPlaceholder}
                                />
                                <AppForm.FieldWarningMessage />
                                {redirectToForgotPassword && (
                                    <AemButton
                                        type='button'
                                        textClassName='w-full text-12 leading-[24px] tracking-0 text-primary'
                                        className='m-0 mt-2 p-0 text-left'
                                        onClick={redirectToForgotPassword}
                                        aria-label={forgotPasswordButton?.alt}
                                        {...forgotPasswordButton}
                                    />
                                )}
                            </>
                        )}
                    </AppForm.Field>
                </AppForm.Body>
            </div>

            <AemButton
                primary
                light
                aria-label={loginButton?.alt}
                type='submit'
                className={twx('m-auto mt-8 bg-primary-dark ml:mt-6 ml:w-full', {'mb-5': loginError})}
                {...loginButton}
            />
        </AppForm>
    );
};

export default LoginForm;
